import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Bar, BarChart, Cell, Legend, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MONTH_MOVE_LEGEND, monthMoveClass, monthMoves, SKU_DRAWER_WIDTH } from "@/lib/monthMove";

import { getHistory, getOtherCostMonths, type Component, type HistoryDetail, type HistoryMonth, type Selection } from "./api";
import { COMPONENT_LABEL, COPY, historyChannel, historyStartFor, messageFor, money } from "./format";
import { chartRows } from "./forms";
import { blockProjection, closesMonthsBack, expectedFeeChartRow, feeBlocks, feeChartSeries, feeSignedChartRows, feeTotal, hasFees, padHistoryMonths, projectionTotal, projectionWindow, rowProjection, usedProjectionMonths, weightLabel, type FeeBlock, type ProjectionWindow } from "./historyFeesModel";

const AMAZON: Component[] = ["referral", "dsf", "fba_per_unit", "other"];
const SHOPIFY: Component[] = ["pick_pack", "packaging", "courier", "other"];
const LINE_COLOR: Record<Component, string> = {
  referral: "var(--color-ks-purple-1)",
  dsf: "var(--color-ks-purple-2)",
  fba_per_unit: "var(--color-ks-purple-3)",
  pick_pack: "var(--color-ks-purple-1)",
  packaging: "var(--color-ks-purple-2)",
  courier: "var(--color-ks-purple-3)",
  other: "var(--color-ks-purple-4)",
};
// C5 v2.6: the all-fees chart cycles the five purple tokens over its bars.
const FEE_BAR_TOKENS = ["var(--color-ks-purple-1)", "var(--color-ks-purple-2)", "var(--color-ks-purple-3)", "var(--color-ks-purple-4)", "var(--color-ks-purple-5)"];
const EXPECTED_HEADLINE = "bg-ks-purple-4 font-semibold text-ks-purple";
// C4 v1.6.5: the row labels and the Expected column stay put while the month columns scroll (same method as the tab's
// PINNED_COLUMN_CLASS). The edges are inset shadows, not borders (border-collapse can drop a border from a sticky cell).
const PINNED_LABEL = "sticky left-0 z-10 bg-background shadow-[inset_-1px_0_0_var(--color-ks-grey-4)]";
const PINNED_EXPECTED = "sticky right-0 z-10 shadow-[inset_1px_0_0_var(--color-ks-grey-4)]";

export function tooltipValue(value: unknown): string {
  return typeof value === "number" ? value.toFixed(2) : String(value);
}

export function unitsPerOrder(month: { units: number; orders: number | null }): string {
  return month.orders ? (month.units / month.orders).toFixed(1) : "—";
}

// A value that rounds to 0.00 prints as £0.00, never £-0.00 (small negative leaves such as a refund of 0.0007 per unit).
function perUnitCell(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return money(String(Math.abs(value) < 0.005 ? 0 : value), currency);
}

function expectedHeader(window: ProjectionWindow, used: string[]): string {
  if (!window || used.length === 0) return "Expected (3-month trailing)";
  if (used.length === 3) return `Expected (3-month trailing) - ${weightLabel(used)}`;
  if (used.length === 2) return `Expected (3-month trailing) - 2 months used, ${weightLabel(used)}`;
  return "Expected (3-month trailing) - 1 month used";
}

function monthsUsedText(window: ProjectionWindow, used: string[]): string {
  if (!window) return "No closed month in view.";
  if (used.length === 0) return "No units sold in the closed months in view.";
  const dropped = window.window.filter((month) => !used.includes(month));
  return `Months used: ${used.join(", ")}.${dropped.length ? ` Left out, no units: ${dropped.join(", ")}.` : ""}`;
}

function noProjectionTitle(w: ProjectionWindow, used: string[]): string {
  if (!w) return "No closed month in view.";
  if (used.length === 0) return "No units sold in the closed months in view.";
  return used.length === 1 ? `No fee in ${used[0]}.` : `No fee in ${used[0]} to ${used[used.length - 1]}.`;
}

function projectionCell(value: number | null, currency: string | null | undefined, w: ProjectionWindow, used: string[]) {
  return <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} title={value === null ? noProjectionTitle(w, used) : undefined}>{perUnitCell(value, currency)}</TableCell>;
}

// C5 v2.6 (spec B, B11): one block per engine group in the engine's order. Every block shows a bold subtotal row
// (sum of its rows) and its rows indented under it, and collapses from that row. C5 v2.8 (spec B v1.2): a sub-row
// (parent_code) sits one level further under its leaf and counts in no sum. Total Fee sums every row without a parent.
// Read-only: no row is hidden except by the viewer's collapse.
export function HistoryFeesTable({ blocks, months, currency, caption, collapsed, onToggle, projection, noData = new Set<string>(), unit = "unit" }: {
  blocks: FeeBlock[]; months: HistoryMonth[]; currency: string | null | undefined; caption: string;
  collapsed: ReadonlySet<string>; onToggle: (group: string) => void;
  projection?: ProjectionWindow;
  noData?: ReadonlySet<string>;
  unit?: "unit" | "case";
}) {
  const showOrders = months.some((month) => month.orders !== null);
  const showProjection = projection !== undefined;
  const used = projection ? usedProjectionMonths(months, projection) : [];
  const totalMoves = monthMoves(months.map((month) => feeTotal(blocks, month)));
  return (
    <>
      <Table>
        <TableCaption>{caption}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead className={PINNED_LABEL} />
            {months.map((month) => <TableHead key={month.month} title={noData.has(month.month.slice(0, 7)) ? `No fee for this SKU in ${month.month.slice(0, 7)}.` : undefined}>{month.month}{noData.has(month.month.slice(0, 7)) && <Badge variant="outline" className="ml-1">no data</Badge>}</TableHead>)}
            {showProjection && <TableHead title={`Fee per ${unit} over the last three closed months in view, weighted 50% the newest month, 30% the month before, 20% the oldest; rescaled to sum to 100% when fewer months are in view. A month without a fee counts as zero. Computed from the fees PFC serves. ${monthsUsedText(projection ?? null, used)}`} className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`}>{expectedHeader(projection ?? null, used)}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {blocks.map((block) => {
            const isCollapsed = block.subtotal && collapsed.has(block.group);
            const blockMoves = monthMoves(months.map((month) => block.subtotalPerUnit[month.month]));
            return (
              <Fragment key={block.group}>
                {block.subtotal && <TableRow className="font-bold">
                  <TableHead scope="row" className={`${PINNED_LABEL} font-bold`}>
                    <Button type="button" variant="ghost" size="xs" className="font-bold" aria-expanded={!isCollapsed} onClick={() => onToggle(block.group)}>
                      {isCollapsed ? <ChevronRight aria-hidden /> : <ChevronDown aria-hidden />}{block.label}
                    </Button>
                  </TableHead>
                  {months.map((month, index) => <TableCell key={month.month} className={monthMoveClass(blockMoves[index] ?? null)}>{perUnitCell(block.subtotalPerUnit[month.month], currency)}</TableCell>)}
                  {showProjection && projectionCell(projection ? blockProjection(block, months, projection) : null, currency, projection ?? null, used)}
                </TableRow>}
                {!isCollapsed && block.rows.map((row) => {
                  const rowMoves = monthMoves(months.map((month) => row.perUnit[month.month]));
                  return (
                    <TableRow key={row.code}>
                      <TableHead scope="row" className={`${PINNED_LABEL} ${row.parent ? "pl-8" : "pl-14"} font-normal`} data-sub-row={row.parent ? undefined : true}>{row.label}</TableHead>
                      {months.map((month, index) => <TableCell key={month.month} className={monthMoveClass(rowMoves[index] ?? null)}>{perUnitCell(row.perUnit[month.month], currency)}</TableCell>)}
                      {showProjection && projectionCell(projection ? rowProjection(row, months, projection) : null, currency, projection ?? null, used)}
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          })}
          <TableRow className="font-bold">
            <TableHead scope="row" className={`${PINNED_LABEL} font-bold`}>Total Fee</TableHead>
            {months.map((month, index) => <TableCell key={month.month} className={monthMoveClass(totalMoves[index] ?? null)}>{perUnitCell(feeTotal(blocks, month), currency)}</TableCell>)}
            {showProjection && projectionCell(projection ? projectionTotal(blocks, months, projection) : null, currency, projection ?? null, used)}
          </TableRow>
          <TableRow>
            <TableHead scope="row" className={PINNED_LABEL}>{unit === "case" ? "Cases" : "Units"}</TableHead>
            {months.map((month) => <TableCell key={month.month}>{noData.has(month.month.slice(0, 7)) ? "—" : month.units.toLocaleString("en-GB")}</TableCell>)}
            {showProjection && <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} />}
          </TableRow>
          {showOrders && <TableRow>
            <TableHead scope="row" className={PINNED_LABEL}>Orders</TableHead>
            {months.map((month) => <TableCell key={month.month}>{month.orders === null ? "—" : month.orders.toLocaleString("en-GB")}</TableCell>)}
            {showProjection && <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} />}
          </TableRow>}
          {showOrders && <TableRow>
            <TableHead scope="row" className={PINNED_LABEL}>{unit === "case" ? "Cases per order" : "Units per order"}</TableHead>
            {months.map((month) => <TableCell key={month.month}>{unitsPerOrder(month)}</TableCell>)}
            {showProjection && <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} />}
          </TableRow>}
        </TableBody>
      </Table>
      <p className="text-sm text-ks-grey-1">{MONTH_MOVE_LEGEND}</p>
      {showProjection && <p className="text-ks-ink text-sm">Expected = fee per {unit} over the last three closed months in view, weighted 50/30/20 from the newest month to the oldest (rescaled when fewer months are in view); a month without a fee counts as zero. Precision Pricing still prices fees on the PFC expected feed (rate cards for order-driven fees, the 12-month base for fixed Other) until Damien decides on trailing 3. {monthsUsedText(projection ?? null, used)}</p>}
    </>
  );
}

export type StackBar = { key: string; name: string; fill: string; credit: boolean };

// C4 v1.6.3: one stacked bar per month. stackOffset="sign" stacks every positive segment up from zero and every negative
// segment down from zero. A row with expected: 1 is the Expected (trailing 3) value (DEC-1380 PB-54): lighter fill and a
// dashed outline, the same look as the Sold Costs chart. Total Fee stays in the table; it is not a bar.
export function FeeStackChart({ title, rows, bars }: { title: string; rows: Array<Record<string, string | number | null>>; bars: StackBar[] }) {
  return (
    <ChartFrame title={title} pointCount={rows.length * bars.length} emptyMessage={COPY.historyEmpty}>
      <BarChart data={rows} stackOffset="sign">
        <XAxis dataKey="month" /><YAxis /><Tooltip formatter={tooltipValue} /><Legend />
        {bars.map((bar) => (
          <Bar key={bar.key} dataKey={bar.key} name={bar.name} stackId="cost" fill={bar.fill} legendType={bar.credit ? "none" : "rect"} isAnimationActive={false}>
            {rows.map((row) => <Cell key={`${bar.key}:${row.month}`} fillOpacity={row.expected ? 0.35 : bar.credit ? 0.5 : 1} stroke={row.expected ? bar.fill : undefined} strokeDasharray={row.expected ? "4 3" : undefined} />)}
          </Bar>
        ))}
      </BarChart>
    </ChartFrame>
  );
}

export function signedBars(series: Array<{ key: string; name: string }>): StackBar[] {
  return series.flatMap((line, index) => [
    { key: `${line.key}:costs`, name: line.name, fill: FEE_BAR_TOKENS[index % FEE_BAR_TOKENS.length], credit: false },
    { key: `${line.key}:credits`, name: `${line.name} (credits)`, fill: FEE_BAR_TOKENS[index % FEE_BAR_TOKENS.length], credit: true },
  ]);
}

export function SkuHistoryDialog({ selected, from, to, thisMonth, onClose }: { selected: Selection; from: string; to: string; thisMonth: string; onClose: () => void }) {
  const start = historyStartFor(selected.channel_location);
  const drawerFrom = from < start ? start : from;
  const beforeStart = drawerFrom > to;
  const detail = useQuery({
    queryKey: ["pfc", "history", "detail", drawerFrom, to, selected],
    queryFn: () => getHistory({ from: drawerFrom, to, ...selected }),
    enabled: !beforeStart,
    retry: 1,
  });
  const monthsBack = closesMonthsBack(drawerFrom, thisMonth);
  const closes = useQuery({ queryKey: ["pfc", "other-costs", "months", monthsBack], queryFn: () => getOtherCostMonths({ months_back: monthsBack }), enabled: !beforeStart, retry: 1 });
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (group: string) => setCollapsed((previous) => {
    const next = new Set(previous);
    if (next.has(group)) next.delete(group); else next.add(group);
    return next;
  });
  const data = detail.data?.data as HistoryDetail | undefined;
  const key = data?.key;
  const perCase = selected.channel_location.startsWith("wholesale_");
  const unit = perCase ? "case" : "unit";
  const components = selected.channel_location.startsWith("amazon_") ? AMAZON : SHOPIFY;
  const rows = chartRows(data, components);
  const months = data?.months ?? [];
  const period = padHistoryMonths(months, drawerFrom, to, key?.currency ?? "");
  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = tableRef.current?.querySelector<HTMLElement>('[data-slot="table-container"]');
    if (box) box.scrollLeft = box.scrollWidth;
  }, [months.length]);
  const showOrders = months.some((month) => month.orders !== null);
  // The choice is per response: any month with fees renders the all-fees layout; none (an engine before B10) renders today's table.
  const allFees = hasFees(months);
  const blocks = allFees ? feeBlocks(months) : [];
  const projection = projectionWindow(months, closes.data?.data.months, selected.channel_location);
  const expectedFeeRow = expectedFeeChartRow(blocks, months, projection);
  const feeRows = [...feeSignedChartRows(blocks, period.months), ...(expectedFeeRow ? [expectedFeeRow] : [])];
  const title = `${key?.xero_sku ?? selected.xero_sku} · ${key ? (key.marketplace_name ?? key.channel_location) : selected.channel_location}, ${beforeStart ? from : drawerFrom} to ${to}`;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={`max-h-[90vh] overflow-y-auto ${SKU_DRAWER_WIDTH}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{allFees ? <>{COPY.historyAllFees(key?.currency ?? null)}{perCase && <> {COPY.historyPerCase}</>}</> : <>Fee per {unit}, {key?.currency ?? ""} net of VAT. {perCase ? "Cases" : "Units"} and orders are those billed in each month, the {unit}s each fee is divided by.</>}</DialogDescription>
        </DialogHeader>
        {drawerFrom !== from && !beforeStart && <p className="text-sm text-ks-grey-1">{COPY.historyDrawerStart(historyChannel(selected.channel_location), start)}</p>}
        {beforeStart ? (
          <p className="text-ks-ink">{COPY.historyBeforeStart(historyChannel(selected.channel_location), start)}</p>
        ) : detail.isPending ? (
          <p>Loading…</p>
        ) : detail.error ? (
          <p role="alert" className="text-ks-danger">{messageFor(detail.error)}</p>
        ) : rows.length === 0 ? (
          <p className="text-ks-ink">{COPY.historyEmpty}</p>
        ) : allFees ? (
          <>
            <div ref={tableRef}><HistoryFeesTable blocks={blocks} months={period.months} currency={key?.currency} caption={`Fee per ${unit} by month, ${key?.xero_sku ?? selected.xero_sku}`} collapsed={collapsed} onToggle={toggle} projection={projection} noData={period.noData} unit={unit} /></div>
            <FeeStackChart title={`Fulfilment cost per ${unit} by month, ${selected.xero_sku} on ${selected.channel_location}`} rows={feeRows} bars={signedBars(feeChartSeries(blocks))} />
          </>
        ) : (
          <>
            <Table>
              <TableCaption>{`Fee per unit by month, ${key?.xero_sku ?? selected.xero_sku}`}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead />
                  {rows.map((row) => <TableHead key={String(row.month)}>{row.month}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {components.map((component) => (
                  <TableRow key={component}>
                    <TableHead scope="row">{COMPONENT_LABEL[component]}</TableHead>
                    {rows.map((row) => <TableCell key={String(row.month)}>{row[component] === null ? "—" : money(String(row[component]), key?.currency)}</TableCell>)}
                  </TableRow>
                ))}
                <TableRow className="font-bold">
                  <TableHead scope="row" className="font-bold">Total Fee</TableHead>
                  {rows.map((row) => <TableCell key={String(row.month)}>{row.total === null ? "—" : money(String(row.total), key?.currency)}</TableCell>)}
                </TableRow>
                <TableRow>
                  <TableHead scope="row">Units</TableHead>
                  {months.map((month) => <TableCell key={month.month}>{month.units.toLocaleString("en-GB")}</TableCell>)}
                </TableRow>
                {showOrders && <TableRow>
                  <TableHead scope="row">Orders</TableHead>
                  {months.map((month) => <TableCell key={month.month}>{month.orders === null ? "—" : month.orders.toLocaleString("en-GB")}</TableCell>)}
                </TableRow>}
                {showOrders && <TableRow>
                  <TableHead scope="row">Units per order</TableHead>
                  {months.map((month) => <TableCell key={month.month}>{unitsPerOrder(month)}</TableCell>)}
                </TableRow>}
              </TableBody>
            </Table>
            <FeeStackChart title={`Fulfilment cost per unit by month, ${selected.xero_sku} on ${selected.channel_location}`} rows={rows} bars={components.map((component) => ({ key: component, name: COMPONENT_LABEL[component], fill: LINE_COLOR[component], credit: false }))} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
